import { pb } from '@/lib/pocketbase';

export interface DiscountCode {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  description?: string;
  usage_limit?: number;
  used_count: number;
  min_order_value?: number;
  max_discount_amount?: number;
  valid_from?: string;
  valid_until?: string;
  applies_to: 'all' | 'bookings' | 'courses';
  is_active: boolean;
}

export interface DiscountValidationResult {
  isValid: boolean;
  error?: string;
  discount?: DiscountCode;
  discountAmount?: number;
  finalAmount?: number;
}

export const validateDiscountCode = async (
  code: string,
  orderAmount: number,
  orderType: 'bookings' | 'courses' = 'bookings'
): Promise<DiscountValidationResult> => {
  if (!code.trim()) {
    return { isValid: false, error: 'Please enter a discount code' };
  }

  try {
    // Find the discount code
    const records = await pb.collection('discount_codes').getList(1, 1, {
      filter: `code = "${code.toUpperCase().trim()}" && is_active = true`,
    });

    if (records.items.length === 0) {
      return { isValid: false, error: 'Invalid discount code' };
    }

    const discount = records.items[0] as unknown as DiscountCode;

    // Check if code applies to this order type
    if (discount.applies_to !== 'all' && discount.applies_to !== orderType) {
      return { 
        isValid: false, 
        error: `This code is only valid for ${discount.applies_to}` 
      };
    }

    // Check validity dates
    const now = new Date();
    if (discount.valid_from && new Date(discount.valid_from) > now) {
      return { isValid: false, error: 'This discount code is not yet active' };
    }
    if (discount.valid_until && new Date(discount.valid_until) < now) {
      return { isValid: false, error: 'This discount code has expired' };
    }

    // Check usage limit
    if (discount.usage_limit && discount.used_count >= discount.usage_limit) {
      return { isValid: false, error: 'This discount code has reached its usage limit' };
    }

    // Check minimum order value
    if (discount.min_order_value && orderAmount < discount.min_order_value) {
      return { 
        isValid: false, 
        error: `Minimum order of ${discount.min_order_value} EGP required` 
      };
    }

    // Calculate discount amount
    let discountAmount = 0;
    if (discount.discount_type === 'percentage') {
      discountAmount = (orderAmount * discount.discount_value) / 100;
      // Apply max discount cap if set
      if (discount.max_discount_amount && discountAmount > discount.max_discount_amount) {
        discountAmount = discount.max_discount_amount;
      }
    } else {
      discountAmount = discount.discount_value;
    }

    // Ensure discount doesn't exceed order amount
    discountAmount = Math.min(discountAmount, orderAmount);
    const finalAmount = orderAmount - discountAmount;

    return {
      isValid: true,
      discount,
      discountAmount,
      finalAmount,
    };
  } catch (error) {
    console.error('Error validating discount code:', error);
    return { isValid: false, error: 'Failed to validate discount code' };
  }
};

export const incrementDiscountUsage = async (discountId: string): Promise<boolean> => {
  try {
    const discount = await pb.collection('discount_codes').getOne(discountId);
    await pb.collection('discount_codes').update(discountId, {
      used_count: (discount.used_count || 0) + 1,
    });
    return true;
  } catch (error) {
    console.error('Error incrementing discount usage:', error);
    return false;
  }
};
